import useProductQueries from '@dashboard/hooks/queries/useProductQueries';
import { ChevronDownIcon, ChevronUpIcon, EllipsisVerticalIcon } from '@heroicons/react/24/outline';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import clsx from 'clsx';
import { FieldArray, Formik } from 'formik';
import { isEmpty, uniqueId } from 'lodash';
import React, { useState } from 'react';
import * as Yup from 'yup';

import Features from '@dashboard/enums/features';
import { DragIcon } from '@dashboard/icons/DragIcon';
import { confirm } from '@dashboard/lib/confirm';
import { transformApiErrorToFormik } from '@dashboard/services/form';
import { formatPriceInput } from '@dashboard/services/formatting';
import getCapitalizedSubscriptionInterval from '@dashboard/services/formatting/getCapitalizedSubscriptionInterval';
import { formatPriceForInput } from '@dashboard/services/formatting/price';
import { PRODUCT_TYPES } from '@dashboard/services/getProductType';
import logException from '@dashboard/services/logException';

import Button from '@dashboard/components/Button';
import CanUseFeature from '@dashboard/components/CanUseFeature';
import Collapsible from '@dashboard/components/Collapsible';
import { FormElement, Label, TextArea } from '@dashboard/components/Form';
import {
  Checkbox,
  FormSubmit,
  Listbox,
  Radio,
  Select,
  TextInput,
  Textarea,
} from '@dashboard/components/Formik';
import FormError, { FormErrorBase } from '@dashboard/components/Formik/components/FormError';
import { ImageField } from '@dashboard/components/Formik/components/Image';
import Link from '@dashboard/components/Link';
import Lozenge from '@dashboard/components/Lozenge';
import Reorderable from '@dashboard/components/Reorderable';
import Tooltip, { InfoTooltip } from '@dashboard/components/Tooltip';

import useProduct from '../../../useProduct';
import { useVariantHelpers } from '../useVariantHelpers';
import { getInitialVariantOption } from '../useVariantOption';
import { useVariantEdit } from '../VariantEditContext';
import { useVariants } from '../VariantsContext';
import ShowHideLogicField from './ShowHideLogicField';

const getValidationSchema = product =>
  Yup.object().shape({
    options: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required('Required'),
        description: Yup.string(),
        type: Yup.string().required('Type is required').oneOf(['one_time', 'recurring']),
        additionalChargeAmount: Yup.string()
          .test('formats-to-number', 'Should be a number', value => {
            const formattedValue = formatPriceInput(value, product.currency);

            if (typeof formattedValue === 'number') {
              return true;
            }

            return false;
          })
          .nullable()
          .test('is-positive', 'Positive price only', value => {
            const formattedValue = formatPriceInput(value, product.currency);

            if (
              product.type === PRODUCT_TYPES.SUBSCRIPTION ||
              product.type === PRODUCT_TYPES.PAYMENT_PLAN
            ) {
              return !formattedValue || formattedValue >= 0;
            }

            return true;
          }),
      }),
    ),
  });

const getInitialValues = (variant, currency) => ({
  name: variant.name,
  required: variant.required,
  options: variant.options.map(option => ({
    ...option,
    additionalChargeAmount:
      option.additionalChargeAmount &&
      formatPriceForInput({
        amount: option.additionalChargeAmount,
        currency: currency,
        style: 'decimal',
      }),
    discountedFromPrice:
      option.discountedFromPrice &&
      formatPriceForInput({
        amount: option.discountedFromPrice,
        currency: currency,
        style: 'decimal',
      }),
  })),
  manageVariantOptionStock: variant.manageVariantOptionStock,
  reference: variant.reference,
  selectionType: variant.selectionType,
  increasesWithQuantity: variant.increasesWithQuantity,
  preselect: variant.preselect,
  showHideLogic: variant.showHideLogic,
  hidden: variant.hidden,
  layout: {
    variantOptionLayout: variant.layout?.variantOptionLayout || 'list',
    spacing: variant.layout?.spacing || 'comfortable',
    collapseVariantOptions:
      typeof variant.layout?.collapseVariantOptions === 'boolean'
        ? variant.layout?.collapseVariantOptions
        : false,
    variantOptionColumns:
      typeof variant.layout?.variantOptionColumns === 'number'
        ? variant.layout?.variantOptionColumns
        : 2,
    showVariantName:
      typeof variant.layout?.showVariantName === 'boolean' ? variant.layout?.showVariantName : true,
    showVariantOptionNames:
      typeof variant.layout?.showVariantOptionNames === 'boolean'
        ? variant.layout?.showVariantOptionNames
        : true,
    showVariantOptionPrices:
      typeof variant.layout?.showVariantOptionPrices === 'boolean'
        ? variant.layout?.showVariantOptionPrices
        : true,
    showVariantOptionPriceSign:
      typeof variant.layout?.showVariantOptionPriceSign === 'boolean'
        ? variant.layout?.showVariantOptionPriceSign
        : false,
    textAlign: typeof variant.layout?.textAlign === 'string' ? variant.layout?.textAlign : 'left',
    imageSize: typeof variant.layout?.imageSize === 'string' ? variant.layout?.imageSize : 'small',
  },
});

export default function VariantEdit({ variant }) {
  const { product } = useProduct();
  const { saveVariant } = useProductQueries();

  const { dispatch } = useVariants();

  const { onSaveVariant, onDirty, onCancelAddVariant } = useVariantHelpers(
    variant._id || variant.key,
  );

  const { setIsEditingVariant } = useVariantEdit();

  const [layoutOpen, setLayoutOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const variantId = variant._id || variant.key;

  const validationSchema = getValidationSchema(product);
  const initialValues = getInitialValues(variant, product.currency);

  async function onFormSubmit(values, actions) {
    actions.setStatus(undefined);

    const body = {
      ...values,
      status: variant.status,
      options: values.options.map(option => ({
        ...option,
        additionalChargeAmount:
          option.additionalChargeAmount &&
          formatPriceInput(option.additionalChargeAmount, product.currency),
        discountedFromPrice:
          option.discountedFromPrice &&
          formatPriceInput(option.discountedFromPrice, product.currency),
      })),
    };

    delete body.isEditing;
    delete body.isNew;

    const { data, error } = await saveVariant(body, variant?._id, product._id);

    if (error) {
      logException(error);
      const { status, errors } = transformApiErrorToFormik(error);

      if (!isEmpty(errors)) {
        actions.setErrors(errors);
      } else {
        actions.setStatus(status);
      }
    } else {
      actions.setStatus('');

      actions.resetForm({
        values: values,
      });

      dispatch({
        type: 'update',
        variant,
        values: {
          ...data,
          isEditing: true,
        },
      });

      onSaveVariant();
    }
  }

  return (
    <div>
      <Formik
        key={variantId}
        validationSchema={validationSchema}
        validateOnBlur={false}
        validateOnChange={false}
        initialValues={initialValues}
        onSubmit={onFormSubmit}
      >
        {({ handleSubmit, dirty, status, values, setFieldValue }) => {
          onDirty(dirty);

          return (
            <form onSubmit={handleSubmit} key={variantId}>
              <FormElement>
                <Label htmlFor={`variants.${variantId}.name`}>Variant name</Label>
                <TextInput
                  id={`variants.${variantId}.name`}
                  name="name"
                  type="text"
                  placeholder="Name"
                  autoFocus
                />
                <FormError name="name" />
              </FormElement>

              <Label>Options</Label>

              <div className={`mb-4`}>
                <FieldArray
                  name="options"
                  render={arrayHelpers => {
                    function onAddVariantOption() {
                      arrayHelpers.push(getInitialVariantOption(product.type));
                    }
                    return (
                      <>
                        <Reorderable.Wrapper>
                          <Reorderable
                            id={`variants.${variantId}.options`}
                            items={values?.options}
                            onReorder={options => setFieldValue('options', options)}
                            renderItem={({ index, isDragging, dragHandleProps }) => {
                              async function onDeleteVariantOption() {
                                const del = await confirm('Delete this option?', {
                                  confirm: 'Delete',
                                  ...(values.options[index].files?.length > 0 && {
                                    description:
                                      'Past customers might lose access to attached files. Disable this option to avoid this.',
                                  }),
                                });

                                if (del) {
                                  arrayHelpers.remove(index);
                                }
                              }

                              function onEnableDisableVariantOption() {
                                const status =
                                  values.options[index].status === 'enabled'
                                    ? 'disabled'
                                    : 'enabled';

                                arrayHelpers.replace(index, {
                                  ...values.options[index],
                                  status,
                                });

                                if (status === 'enabled') {
                                  setFieldValue(`options[${index}].status`, 'enabled');
                                } else {
                                  setFieldValue(`options[${index}].status`, 'disabled');
                                }
                              }

                              function onDuplicateVariantOption() {
                                const newOption = {
                                  ...values.options[index],
                                  name: `${values.options[index].name} (copy)`,
                                  key: uniqueId(),
                                };

                                delete newOption._id;
                                delete newOption.id;
                                delete newOption.stripeProductId;
                                delete newOption.stripePriceId;

                                arrayHelpers.insert(index + 1, newOption);
                              }

                              const [additionalSettingsOpen, setAdditionalSettingsOpen] =
                                useState(false);

                              return (
                                <Reorderable.Item
                                  {...dragHandleProps}
                                  isLast={index === values.options.length - 1}
                                  isDragging={isDragging}
                                  border={false}
                                >
                                  <div className={clsx(`flex w-full mb-4`)}>
                                    <div className="min-w-0">
                                      <div className="pr-2.5 py-2">
                                        <DragIcon {...dragHandleProps} className="w-4 h-4" />
                                      </div>
                                    </div>

                                    <div
                                      className={clsx('flex-1 min-w-0 relative', {
                                        'pointer-events-none':
                                          values.options[index].status === 'disabled',
                                      })}
                                    >
                                      {values.options[index].status === 'disabled' && (
                                        <div className="absolute top-0 left-0 w-full h-full z-10 bg-white/60 flex items-center justify-center cursor-not-allowed pointer-events-none"></div>
                                      )}

                                      <div className={clsx('flex gap-x-2 mb-3', {})}>
                                        <div className="w-[63px] h-[63px]">
                                          <ImageField
                                            id={`options[${index}].image`}
                                            name={`options[${index}].image`}
                                            image={values.options[index].image}
                                            previewSize="variant_option"
                                            variant="minimal"
                                          />
                                        </div>

                                        <div className="flex-1">
                                          {values.options[index].status === 'disabled' && (
                                            <div className="flex justify-end items-start mb-1">
                                              <Tooltip label="Disabled options will not be shown to customers">
                                                <Lozenge appearance="default">Disabled</Lozenge>
                                              </Tooltip>
                                            </div>
                                          )}

                                          <TextInput
                                            id={`options[${index}].name`}
                                            name={`options[${index}].name`}
                                            type="text"
                                            placeholder="Name (supports HTML)"
                                            className="rounded-b-none focus:z-10 text-sm !py-[6px] relative"
                                            required
                                            border
                                            autoFocus={!!values.name && !values.options[index].name}
                                          />

                                          <Textarea
                                            id={`options[${index}].description`}
                                            name={`options[${index}].description`}
                                            className="placeholder-gray-300 border-t-0 rounded-t-none -mt-px focus:z-10 text-sm !py-[6px] relative"
                                            placeholder="Description (supports HTML)"
                                            value={values.options[index].description || undefined}
                                            autosize={true}
                                            minRows={1}
                                          />
                                        </div>
                                      </div>

                                      <div
                                        className={clsx('flex-1 grid grid-cols-2 gap-2 ml-[71px]')}
                                      >
                                        <div className="col-span-1">
                                          <Label
                                            htmlFor={`options[${index}].additionalChargeAmount`}
                                          >
                                            Price
                                          </Label>

                                          <CanUseFeature feature={Features.VARIANT_PRICE} inset>
                                            <TextInput
                                              id={`options[${index}].additionalChargeAmount`}
                                              name={`options[${index}].additionalChargeAmount`}
                                              className="text-sm !py-[6px]"
                                              type="text"
                                              placeholder={
                                                product.type === PRODUCT_TYPES.SUBSCRIPTION ||
                                                product.type === PRODUCT_TYPES.PAYMENT_PLAN
                                                  ? 'Price'
                                                  : 'Price (+/-)'
                                              }
                                              border
                                            />
                                          </CanUseFeature>
                                        </div>

                                        <div className="col-span-1">
                                          <Label
                                            htmlFor={`options[${index}].discountedFromPrice`}
                                            className="whitespace-nowrap truncate"
                                          >
                                            Discounted from
                                          </Label>

                                          <CanUseFeature feature={Features.VARIANT_PRICE} inset>
                                            <TextInput
                                              id={`options[${index}].discountedFromPrice`}
                                              name={`options[${index}].discountedFromPrice`}
                                              className="text-sm !py-[6px]"
                                              type="text"
                                              placeholder="Discounted from"
                                              border
                                            />
                                          </CanUseFeature>
                                        </div>

                                        {values.manageVariantOptionStock && (
                                          <div className="col-span-1">
                                            <Label htmlFor={`options[${index}].stock`}>Stock</Label>
                                            <TextInput
                                              id={`options[${index}].stock`}
                                              name={`options[${index}].stock`}
                                              className="text-sm !py-[6px]"
                                              type="number"
                                              placeholder="Stock"
                                              border
                                            />
                                          </div>
                                        )}
                                      </div>

                                      <div className="ml-[71px] mt-4">
                                        <Collapsible.Root
                                          open={additionalSettingsOpen}
                                          onOpenChange={setAdditionalSettingsOpen}
                                        >
                                          <Collapsible.Trigger className="flex items-center gap-x-1 text-gray-900 hover:text-gray-500 font-medium text-sm">
                                            <div className="flex-1 text-left">
                                              Additional settings
                                            </div>
                                            {additionalSettingsOpen ? (
                                              <ChevronUpIcon className="w-4 h-4" />
                                            ) : (
                                              <ChevronDownIcon className="w-4 h-4" />
                                            )}
                                          </Collapsible.Trigger>

                                          <Collapsible.Content className="CollapsibleContentAnimation mt-2">
                                            <div className="w-full flex-1 grid grid-cols-2 gap-x-2 gap-y-4">
                                              <div className="col-span-1">
                                                <Label htmlFor={`options[${index}].sku`}>SKU</Label>
                                                <TextInput
                                                  id={`options[${index}].sku`}
                                                  name={`options[${index}].sku`}
                                                  className="text-sm !py-[6px]"
                                                  type="text"
                                                  placeholder="SKU"
                                                  border
                                                />
                                              </div>

                                              {(product.type === PRODUCT_TYPES.SUBSCRIPTION ||
                                                product.type === PRODUCT_TYPES.PAYMENT_PLAN) && (
                                                <div className="col-span-1">
                                                  <Label htmlFor={`options[${index}].type`}>
                                                    Billing
                                                  </Label>
                                                  <Select
                                                    id={`options[${index}].type`}
                                                    name={`options[${index}].type`}
                                                    placeholder="Billing model"
                                                    className="text-sm !py-[6px]"
                                                  >
                                                    <option value="recurring">
                                                      {product.planIterations > 1 ? (
                                                        <>
                                                          {'Payment plan — '}
                                                          {getCapitalizedSubscriptionInterval(
                                                            product.interval,
                                                            product.intervalCount,
                                                          )}
                                                        </>
                                                      ) : (
                                                        getCapitalizedSubscriptionInterval(
                                                          product.interval,
                                                          product.intervalCount,
                                                        )
                                                      )}
                                                    </option>
                                                    <option value="one_time">One-time</option>
                                                  </Select>
                                                </div>
                                              )}

                                              <div className="col-span-2">
                                                <FormElement className="!mb-0">
                                                  <Label htmlFor="files">File downloads</Label>

                                                  <Listbox
                                                    id={`options[${index}].files`}
                                                    name={`options[${index}].files`}
                                                    options={product.files?.map(file => ({
                                                      value: file._id,
                                                      name: file.name,
                                                    }))}
                                                    placeholder={
                                                      product.files?.length >= 1
                                                        ? 'Select files to attach'
                                                        : 'First upload files in the Files tab'
                                                    }
                                                    disabled={
                                                      !product.files || product.files.length === 0
                                                    }
                                                    multiple
                                                    selectAll
                                                    selectAllLabel="All files"
                                                  />
                                                </FormElement>
                                              </div>
                                            </div>
                                          </Collapsible.Content>
                                        </Collapsible.Root>
                                      </div>

                                      <FormError name={`options[${index}].name`} />
                                      <FormError name={`options.${index}.additionalChargeAmount`} />
                                      <FormError name={`options[${index}].sku`} />
                                      <FormError name={`options[${index}].stock`} />
                                      <FormError name={`options[${index}].image`} />
                                    </div>

                                    <div className="items-center self-start ml-2 min-w-0">
                                      <DropdownMenu.Root>
                                        <DropdownMenu.Trigger asChild>
                                          <Button
                                            aria-label="more options"
                                            appearance="icon-button"
                                            size="narrow"
                                          >
                                            <EllipsisVerticalIcon className="w-4 h-4" />
                                          </Button>
                                        </DropdownMenu.Trigger>

                                        <DropdownMenu.Portal>
                                          <DropdownMenu.Content
                                            className="origin-top-right absolute right-0 z-20 mt-2 w-40 rounded-md shadow-lg bg-white ring-1 ring-gray-100 focus:outline-none overflow-hidden"
                                            side="bottom"
                                            align="end"
                                          >
                                            <DropdownMenu.Item
                                              onClick={onDuplicateVariantOption}
                                              className="p-2 outline-none hover:bg-gray-100 cursor-pointer text-gray-900"
                                            >
                                              Duplicate
                                            </DropdownMenu.Item>
                                            <DropdownMenu.Item
                                              onClick={onEnableDisableVariantOption}
                                              className="p-2 outline-none hover:bg-gray-100 cursor-pointer text-gray-900"
                                            >
                                              {values.options[index].status === 'enabled'
                                                ? 'Disable'
                                                : 'Enable'}
                                            </DropdownMenu.Item>
                                            <DropdownMenu.Item
                                              onClick={onDeleteVariantOption}
                                              className="p-2 outline-none hover:bg-gray-100 cursor-pointer text-gray-900"
                                            >
                                              Delete
                                            </DropdownMenu.Item>
                                          </DropdownMenu.Content>
                                        </DropdownMenu.Portal>
                                      </DropdownMenu.Root>
                                    </div>
                                  </div>
                                </Reorderable.Item>
                              );
                            }}
                          />
                        </Reorderable.Wrapper>

                        <Button
                          handleClick={onAddVariantOption}
                          appearance="secondary"
                          type="button"
                          className="ml-[26px] mb-2 mt-2"
                        >
                          + Add option
                        </Button>
                      </>
                    );
                  }}
                />
              </div>

              <Collapsible.Root
                open={settingsOpen}
                onOpenChange={open => {
                  if (open) {
                    setSettingsOpen(true);
                    setLayoutOpen(false);
                  } else {
                    setSettingsOpen(false);
                  }
                }}
              >
                <Collapsible.Trigger className="flex items-center gap-x-1 mt-4 text-gray-700 hover:text-gray-900 font-medium text-sm">
                  <>
                    <div className="flex-1 text-left">Settings</div>

                    {settingsOpen ? (
                      <ChevronUpIcon className="w-4 h-4" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4" />
                    )}
                  </>
                </Collapsible.Trigger>

                <Collapsible.Content className="CollapsibleContentAnimation mt-4">
                  <FormElement className="space-y-1.5">
                    <Checkbox.Wrapper>
                      <Checkbox id={`variants.${variantId}.required`} name="required" />
                      <Checkbox.Label htmlFor={`variants.${variantId}.required`}>
                        Required
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <FormError name="required" />

                    <Checkbox.Wrapper>
                      <Checkbox
                        id={`variants.${variantId}.preselect.enabled`}
                        name="preselect.enabled"
                      />
                      <Checkbox.Label htmlFor={`variants.${variantId}.preselect.enabled`}>
                        Preselect option
                      </Checkbox.Label>
                      <FormError name="preselect.enabled" />
                    </Checkbox.Wrapper>

                    {values.preselect?.enabled && (
                      <>
                        <FormElement className="ml-[26px]">
                          <Select
                            id={`variants.${variantId}.preselect.optionId`}
                            name="preselect.optionId"
                          >
                            <option value="" default>
                              Select option
                            </option>
                            {values.options.map((option, index) => (
                              <option key={index} value={option._id || option.key}>
                                {option.name}
                              </option>
                            ))}
                          </Select>
                          <FormError name="preselect.optionId" />
                        </FormElement>

                        {values.selectionType === 'quantity' && (
                          <FormElement className="ml-[26px]">
                            <Label htmlFor={`variants.${variantId}.preselect.quantity`}>
                              Preselect quantity
                            </Label>

                            <TextInput
                              type="number"
                              id={`variants.${variantId}.preselect.quantity`}
                              name="preselect.quantity"
                              placeholder="Preselect quantity"
                            />
                            <FormError name="preselect.quantity" />
                          </FormElement>
                        )}
                      </>
                    )}

                    <Checkbox.Wrapper>
                      <Checkbox
                        id={`variants.${variantId}.showHideLogic.enabled`}
                        name="showHideLogic.enabled"
                      />
                      <Checkbox.Label htmlFor={`variants.${variantId}.showHideLogic.enabled`}>
                        Conditional logic{' '}
                        <InfoTooltip>
                          {`Show this variant based on another variant's
                              selection.`}{' '}
                          <Link
                            to="/help/articles/conditional-logic-for-product-variants"
                            target="_blank"
                            variant="light"
                          >
                            Learn more
                          </Link>
                        </InfoTooltip>
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <FormError name="showHideLogic.enabled" />

                    {values.showHideLogic?.enabled && (
                      <ShowHideLogicField
                        variantId={variantId}
                        variant={values}
                        product={product}
                      />
                    )}

                    <Checkbox.Wrapper>
                      <Checkbox id={`variants.${variantId}.hidden`} name="hidden" />
                      <Checkbox.Label htmlFor={`variants.${variantId}.hidden`}>
                        Hide variant{' '}
                        <InfoTooltip>
                          Hide this variant and show or fill it with query parameters.{' '}
                          <Link
                            to="/help/articles/preselect-checkout-variants"
                            target="_blank"
                            variant="light"
                          >
                            Learn more
                          </Link>
                        </InfoTooltip>
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <FormError name="hidden" />
                  </FormElement>

                  <FormElement>
                    <Label htmlFor={`variants.${variantId}.selectionType`}>Variant selection</Label>

                    <Radio.Group>
                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.selectionType.single`}
                          name="selectionType"
                          value="single"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.selectionType.single`}>
                          Single select
                        </Radio.Label>
                      </Radio.Wrapper>

                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.selectionType.multiple`}
                          name="selectionType"
                          value="multiple"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.selectionType.multiple`}>
                          Multiple select
                        </Radio.Label>
                      </Radio.Wrapper>

                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.selectionType.quantity`}
                          name="selectionType"
                          value="quantity"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.selectionType.quantity`}>
                          Quantity select
                        </Radio.Label>
                      </Radio.Wrapper>
                    </Radio.Group>

                    <FormError name="quantity" />
                  </FormElement>

                  {(values.selectionType === 'single' || values.selectionType === 'multiple') && (
                    <FormElement>
                      <Checkbox.Wrapper>
                        <Checkbox
                          id={`variants.${variantId}.increasesWithQuantity`}
                          name="increasesWithQuantity"
                        />
                        <Checkbox.Label htmlFor={`variants.${variantId}.increasesWithQuantity`}>
                          Use checkout quantity{' '}
                          <InfoTooltip>
                            Add a quantity field and let the price of the variant option multiply
                            with the checkout quantity
                          </InfoTooltip>
                        </Checkbox.Label>
                      </Checkbox.Wrapper>
                      <FormError name="increasesWithQuantity" />
                    </FormElement>
                  )}

                  <Label>Inventory</Label>
                  <FormElement className="space-y-1.5">
                    <Checkbox.Wrapper>
                      <Checkbox
                        id={`variants.${variantId}.manageVariantOptionStock`}
                        name="manageVariantOptionStock"
                      />
                      <Checkbox.Label htmlFor={`variants.${variantId}.manageVariantOptionStock`}>
                        Manage stock
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <FormError name="manageVariantOptionStock" />
                  </FormElement>

                  <FormElement>
                    <Label htmlFor={`variants.${variantId}.reference`}>
                      Reference{' '}
                      <InfoTooltip>
                        Use this reference to preselect the variant and to use the selected option
                        in Zapier.{' '}
                        <Link
                          to="/help/articles/preselect-checkout-variants"
                          target="_blank"
                          variant="light"
                        >
                          Learn more
                        </Link>
                      </InfoTooltip>
                    </Label>

                    <TextInput
                      id={`variants.${variantId}.reference`}
                      name="reference"
                      type="text"
                      placeholder="Reference"
                    />
                    <FormError name="reference" />
                  </FormElement>
                </Collapsible.Content>
              </Collapsible.Root>

              <Collapsible.Root
                open={layoutOpen}
                onOpenChange={open => {
                  if (open) {
                    setLayoutOpen(true);
                    setSettingsOpen(false);
                  } else {
                    setLayoutOpen(false);
                  }
                }}
              >
                <Collapsible.Trigger className="flex items-center gap-x-1 mt-4 text-gray-700 hover:text-gray-900 font-medium text-sm">
                  <div className="flex-1 text-left">Layout</div>
                  {layoutOpen ? (
                    <ChevronUpIcon className="w-4 h-4" />
                  ) : (
                    <ChevronDownIcon className="w-4 h-4" />
                  )}
                </Collapsible.Trigger>

                <Collapsible.Content className="CollapsibleContentAnimation mt-4">
                  <FormElement className="space-y-1.5">
                    <Label htmlFor={`variants.${variantId}.layout.variantOptionLayout`}>
                      Display as
                    </Label>
                    <Radio.Group>
                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.variantOptionLayout.list`}
                          name="layout.variantOptionLayout"
                          value="list"
                        />
                        <Radio.Label
                          htmlFor={`variants.${variantId}.layout.variantOptionLayout.list`}
                        >
                          List
                        </Radio.Label>
                      </Radio.Wrapper>

                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.variantOptionLayout.grid`}
                          name="layout.variantOptionLayout"
                          value="grid"
                        />
                        <Radio.Label
                          htmlFor={`variants.${variantId}.layout.variantOptionLayout.grid`}
                        >
                          Grid
                        </Radio.Label>
                      </Radio.Wrapper>

                      {values.layout?.variantOptionLayout === 'grid' && (
                        <FormElement className="ml-5 mt-2">
                          <div className="w-32">
                            <Select
                              id={`variants.${variantId}.layout.variantOptionColumns`}
                              name="layout.variantOptionColumns"
                            >
                              <option value="1">1 column</option>
                              <option value="2">2 columns</option>
                              <option value="3">3 columns</option>
                              <option value="4">4 columns</option>
                              <option value="5">5 columns</option>
                              <option value="6">6 columns</option>
                              <option value="7">7 columns</option>
                              <option value="8">8 columns</option>
                              <option value="9">9 columns</option>
                              <option value="10">10 columns</option>
                              <option value="11">11 columns</option>
                              <option value="12">12 columns</option>
                            </Select>
                          </div>

                          <FormError name="layout.variantOptionColumns" />
                        </FormElement>
                      )}
                    </Radio.Group>
                    <FormError name="layout.type" />
                  </FormElement>

                  <FormElement>
                    <Checkbox.Wrapper>
                      <Checkbox
                        id={`variants.${variantId}.layout.collapseVariantOptions`}
                        name="layout.collapseVariantOptions"
                      />
                      <Checkbox.Label
                        htmlFor={`variants.${variantId}.layout.collapseVariantOptions`}
                      >
                        Display as accordion{' '}
                        <InfoTooltip>
                          Useful when you have many options or multiple variants
                        </InfoTooltip>
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <FormError name="layout.collapseVariantOptions" />
                  </FormElement>

                  <FormElement className="space-y-1.5">
                    <Label htmlFor="textAlign">Alignment</Label>

                    <Radio.Group>
                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.textAlign.left`}
                          name="layout.textAlign"
                          value="left"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.layout.textAlign.left`}>
                          Default
                        </Radio.Label>
                      </Radio.Wrapper>

                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.textAlign.center`}
                          name="layout.textAlign"
                          value="center"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.layout.textAlign.center`}>
                          Center
                        </Radio.Label>
                      </Radio.Wrapper>

                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.textAlign.stack`}
                          name="layout.textAlign"
                          value="stack"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.layout.textAlign.stack`}>
                          Stack
                        </Radio.Label>
                      </Radio.Wrapper>
                    </Radio.Group>
                  </FormElement>

                  <FormElement className="space-y-1.5">
                    <Label htmlFor="imageSize">Image size</Label>

                    <Radio.Group>
                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.imageSize.small`}
                          name="layout.imageSize"
                          value="small"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.layout.imageSize.small`}>
                          Small
                        </Radio.Label>
                      </Radio.Wrapper>

                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.imageSize.medium`}
                          name="layout.imageSize"
                          value="medium"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.layout.imageSize.medium`}>
                          Medium
                        </Radio.Label>
                      </Radio.Wrapper>

                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.imageSize.large`}
                          name="layout.imageSize"
                          value="large"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.layout.imageSize.large`}>
                          Large
                        </Radio.Label>
                      </Radio.Wrapper>
                    </Radio.Group>
                  </FormElement>

                  <FormElement>
                    <Label htmlFor={`variants.${variantId}.layout.spacing`}>Spacing</Label>
                    <Radio.Group>
                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.spacing.comfortable`}
                          name="layout.spacing"
                          value="comfortable"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.layout.spacing.comfortable`}>
                          Comfortable
                        </Radio.Label>
                      </Radio.Wrapper>

                      <Radio.Wrapper>
                        <Radio.Input
                          id={`variants.${variantId}.layout.spacing.compact`}
                          name="layout.spacing"
                          value="compact"
                        />
                        <Radio.Label htmlFor={`variants.${variantId}.layout.spacing.compact`}>
                          Compact
                        </Radio.Label>
                      </Radio.Wrapper>
                    </Radio.Group>
                    <FormError name="layout.spacing" />
                  </FormElement>

                  <FormElement className="space-y-1.5">
                    <Label>Show / hide</Label>
                    <Checkbox.Wrapper>
                      <Checkbox
                        id={`variants.${variantId}.layout.showVariantName`}
                        name="layout.showVariantName"
                      />
                      <Checkbox.Label htmlFor={`variants.${variantId}.layout.showVariantName`}>
                        Variant name
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <FormError name="layout.showVariantName" />

                    <Checkbox.Wrapper>
                      <Checkbox
                        id={`variants.${variantId}.layout.showVariantOptionNames`}
                        name="layout.showVariantOptionNames"
                      />
                      <Checkbox.Label
                        htmlFor={`variants.${variantId}.layout.showVariantOptionNames`}
                      >
                        Option names
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <FormError name="layout.showVariantOptionNames" />

                    <Checkbox.Wrapper>
                      <Checkbox
                        id={`variants.${variantId}.layout.showVariantOptionPrices`}
                        name="layout.showVariantOptionPrices"
                      />
                      <Checkbox.Label
                        htmlFor={`variants.${variantId}.layout.showVariantOptionPrices`}
                      >
                        Option prices
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <FormError name="layout.showVariantOptionPrices" />

                    <Checkbox.Wrapper>
                      <Checkbox
                        id={`variants.${variantId}.layout.showVariantOptionPriceSign`}
                        name="layout.showVariantOptionPriceSign"
                      />
                      <Checkbox.Label
                        htmlFor={`variants.${variantId}.layout.showVariantOptionPriceSign`}
                      >
                        Plus sign before price
                      </Checkbox.Label>
                    </Checkbox.Wrapper>
                    <FormError name="layout.showVariantOptionPriceSign" />
                  </FormElement>
                </Collapsible.Content>
              </Collapsible.Root>

              <FormErrorBase>{status && status}</FormErrorBase>

              <FormSubmit align="end">
                <FormSubmit.State visible={!!dirty || !variant._id} />
                <FormSubmit.Cancel
                  onClick={() => {
                    if (variant.isNew) {
                      onCancelAddVariant();
                    } else {
                      setIsEditingVariant(false, variant._id || variant.key);
                    }
                  }}
                />
                <FormSubmit.Button>Save</FormSubmit.Button>
              </FormSubmit>
            </form>
          );
        }}
      </Formik>
    </div>
  );
}
